import React from 'react';
import { 
  FiTrendingUp, 
  FiTarget, 
  FiBarChart, 
  FiCheckCircle,
  FiArrowRight,
  FiAward,
  FiUsers,
  FiLayers
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { Image } from '../../components/ui/Image';
import { imageConfig } from '../../config/images';

export const ConsultingPage: React.FC = () => {
  const consultingServices = [
    {
      title: 'Digital Marketing Strategy',
      description: 'Comprehensive digital strategy aligned with your business goals',
      icon: <FiTarget className="text-4xl" />,
      features: [
        'Market Research & Analysis',
        'Competitive Analysis',
        'Customer Persona Development',
        'Channel Strategy',
        'Budget Planning',
        'KPI Definition & Tracking'
      ],
      price: '₹50,000',
      duration: 'one-time',
      deliverables: '40+ page strategy document'
    },
    {
      title: 'SEO Consulting',
      description: 'Expert guidance to improve search rankings and organic traffic',
      icon: <FiBarChart className="text-4xl" />,
      features: [
        'Technical SEO Audit',
        'Keyword Strategy',
        'Content Recommendations',
        'Link Building Strategy',
        'Competitor Analysis',
        'Action Plan & Timeline'
      ],
      price: '₹35,000',
      duration: 'one-time',
      deliverables: 'Comprehensive SEO roadmap'
    },
    {
      title: 'Conversion Rate Optimization',
      description: 'Increase conversions and maximize ROI from existing traffic',
      icon: <FiTrendingUp className="text-4xl" />,
      features: [
        'Website Audit',
        'User Behavior Analysis',
        'A/B Test Planning',
        'Funnel Optimization',
        'UX Recommendations',
        'Implementation Support'
      ],
      price: '₹45,000',
      duration: 'one-time',
      deliverables: 'CRO action plan'
    },
    {
      title: 'Marketing Technology Stack',
      description: 'Select and implement the right marketing tools',
      icon: <FiLayers className="text-4xl" />,
      features: [
        'Technology Assessment',
        'Tool Recommendations',
        'Integration Planning',
        'Implementation Support',
        'Training & Documentation',
        'ROI Calculation'
      ],
      price: '₹60,000',
      duration: 'one-time',
      deliverables: 'Tech stack blueprint'
    },
    {
      title: 'Growth Marketing Consulting',
      description: 'Data-driven growth strategies for rapid scaling',
      icon: <FiTrendingUp className="text-4xl" />,
      features: [
        'Growth Audit',
        'Experimentation Framework',
        'Channel Prioritization',
        'Growth Hacking Tactics',
        'Metrics Dashboard Setup',
        'Ongoing Advisory'
      ],
      price: '₹75,000',
      duration: '/month (3 month minimum)',
      deliverables: 'Monthly growth reports'
    },
    {
      title: 'Marketing Team Training',
      description: 'Upskill your team with latest marketing strategies',
      icon: <FiUsers className="text-4xl" />,
      features: [
        'Customized Training Programs',
        'Workshop Sessions',
        'Best Practices',
        'Tool Training',
        'Q&A Sessions',
        'Training Materials'
      ],
      price: '₹40,000',
      duration: '/day',
      deliverables: 'Training materials & recordings'
    }
  ];

  const consultingPackages = [
    {
      name: 'Strategy Session',
      price: '₹15,000',
      period: '/session',
      description: 'Single focused consulting session',
      features: [
        '2-hour strategy session',
        'Expert consultation',
        'Action plan',
        'Email follow-up',
        'Resource recommendations'
      ],
      popular: false
    },
    {
      name: 'Monthly Retainer',
      price: '₹50,000',
      period: '/month',
      description: 'Ongoing strategic guidance',
      features: [
        '4 consulting hours/month',
        'Bi-weekly check-ins',
        'Strategic planning',
        'Performance analysis',
        'Priority email support',
        'Quarterly strategy review'
      ],
      popular: true
    },
    {
      name: 'Enterprise Consulting',
      price: 'Custom',
      period: 'pricing',
      description: 'Comprehensive consulting engagement',
      features: [
        'Dedicated consultant',
        'Unlimited consulting hours',
        'Team training',
        'Custom reports',
        'Implementation support',
        'Executive presentations',
        '24/7 support'
      ],
      popular: false
    }
  ];

  const results = [
    {
      metric: '200+',
      label: 'Businesses Consulted',
      icon: <FiAward className="text-3xl" />
    },
    {
      metric: '+285%',
      label: 'Average Growth',
      icon: <FiTrendingUp className="text-3xl" />
    },
    {
      metric: '15+ Years',
      label: 'Combined Experience',
      icon: <FiUsers className="text-3xl" />
    },
    {
      metric: '98%',
      label: 'Client Satisfaction',
      icon: <FiCheckCircle className="text-3xl" />
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-32 bg-gradient-to-br from-green-600 via-teal-600 to-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-6xl font-bold mb-8">
            Digital Marketing <span className="text-yellow-400">Consulting</span>
          </h1>
          <p className="text-2xl mb-12 max-w-4xl mx-auto opacity-90">
            Strategic guidance from experienced marketing consultants to accelerate 
            your digital transformation and business growth.
          </p>
          
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="mb-3 flex justify-center text-yellow-400">
                  {result.icon}
                </div>
                <div className="text-3xl font-bold text-yellow-400 mb-2">{result.metric}</div>
                <div className="text-lg">{result.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consulting Expertise Showcase */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 relative">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl p-8 shadow-2xl">
                <Image 
                  src={imageConfig.hero.dashboardPreview} 
                  alt="Marketing Strategy Dashboard" 
                  className="rounded-lg shadow-lg"
                />
              </div>
              <div className="absolute -top-6 -right-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">200+</div>
                  <div className="text-sm text-gray-600">Clients Consulted</div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Strategic Guidance from Industry Experts</h2>
              <p className="text-lg text-gray-600 mb-8">
                Get actionable insights and proven strategies from our team of digital marketing 
                experts who have driven success for hundreds of businesses.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <FiTarget className="text-green-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Custom Strategy Development</h3>
                    <p className="text-gray-600">Tailored marketing strategies aligned with your specific business goals</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <FiUsers className="text-green-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Expert Team</h3>
                    <p className="text-gray-600">15+ years of combined experience across all marketing channels</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <FiTrendingUp className="text-green-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Proven Results</h3>
                    <p className="text-gray-600">Average 285% growth for clients who implement our strategies</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consulting Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Consulting Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert consultation across all aspects of digital marketing
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {consultingServices.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 group">
                <div className="text-green-600 mb-6 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3 text-gray-700">
                      <FiCheckCircle className="text-green-500 text-lg flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="border-t pt-6">
                  <div className="mb-4">
                    <div className="text-2xl font-bold text-green-600">{service.price}</div>
                    <div className="text-sm text-gray-600">{service.duration}</div>
                    <div className="text-sm text-blue-600 mt-1">{service.deliverables}</div>
                  </div>
                  <Link
                    to="/contact"
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    Book Consultation
                    <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consulting Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Consulting Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexible consulting options to match your needs and budget
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {consultingPackages.map((pkg, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-2xl shadow-lg p-8 relative ${
                  pkg.popular ? 'border-2 border-green-500 transform scale-105' : 'border border-gray-200'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-green-600 mb-2">
                    {pkg.price}
                    <span className="text-lg text-gray-600 font-normal">{pkg.period}</span>
                  </div>
                  <p className="text-gray-600">{pkg.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <FiCheckCircle className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.price === 'Custom' ? 'Contact Us' : 'Book Now'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose Our Consulting Services?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get strategic insights from experts who have driven success for 200+ businesses
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiAward className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Expert Guidance</h3>
              <p className="text-gray-600 leading-relaxed">
                15+ years combined experience across all digital marketing channels
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTarget className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Custom Strategies</h3>
              <p className="text-gray-600 leading-relaxed">
                Tailored approaches based on your specific industry, goals, and challenges
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTrendingUp className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Proven Results</h3>
              <p className="text-gray-600 leading-relaxed">
                Average 285% growth for clients who implement our recommendations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Consulting FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Consulting FAQs</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Frequently asked questions about our consulting services
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "What's the difference between consulting and full-service management?",
                answer: "Consulting provides strategic guidance and recommendations that your team implements. Full-service management means we handle execution too. Many clients start with consulting to get a roadmap, then decide on implementation support."
              },
              {
                question: "How long is a typical consulting engagement?",
                answer: "It varies by scope. Strategy sessions are 2-4 hours. Comprehensive audits take 2-3 weeks. Monthly retainers provide ongoing guidance. We'll recommend the best structure based on your needs and goals."
              },
              {
                question: "Do you sign NDAs?",
                answer: "Absolutely! We respect your confidentiality and are happy to sign NDAs before discussing your business details, strategies, or sensitive information."
              },
              {
                question: "Can you help with team training?",
                answer: "Yes! We offer customized training programs for your marketing team. This includes workshops on SEO, PPC, content marketing, analytics, and more. Training can be in-person or virtual."
              },
              {
                question: "What industries do you consult for?",
                answer: "We've worked with businesses across 50+ industries including technology, healthcare, e-commerce, manufacturing, professional services, education, and more. Industry expertise allows us to provide relevant, actionable insights."
              },
              {
                question: "How do you deliver consulting insights?",
                answer: "Deliverables include detailed reports, strategic presentations, action plans with priorities, implementation roadmaps, and recommendations. Everything is documented for easy reference and execution."
              },
              {
                question: "Can you audit my current agency's work?",
                answer: "Yes! We provide unbiased third-party audits of your current marketing efforts, whether managed in-house or by an agency. We'll identify what's working, what's not, and opportunities for improvement."
              },
              {
                question: "Do you offer implementation support?",
                answer: "We can! While pure consulting focuses on strategy, we offer implementation support if needed. This can range from helping launch campaigns to providing ongoing guidance during execution."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-green-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-green-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Transform Your Marketing?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Schedule a free consultation call and discover how our strategic guidance 
            can accelerate your business growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-yellow-400 text-gray-800 px-10 py-5 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-all duration-300 shadow-xl"
            >
              Free Consultation
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-white hover:text-green-600 transition-all duration-300"
            >
              View Case Studies
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};